import styles from "./ForgotPassword.module.scss";
import Button from "components/ui/Button";
import TextInput from "components/ui/TextInput";
import FrameBack from "images/frame_back.svg?react";
import MainLogo from "images/main-logo.svg?react";
import Email from "images/email-forgot.svg?react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChangeEvent, FocusEvent, useEffect, useState } from "react";
import useSignInForm from "./utils";
import Spinner from "components/ui/Spinner";
import { routes } from "pages/routes";
import { forgotPassword } from "../../services/users/auth";
import { useTranslation } from "react-i18next";
import { useToast } from "contexts/ToastContext";
import assert from "utils/assert";
import { useDocumentTitle } from "utils/useDocumentTitle";
import ApiErrorBanner from "components/ApiErrorBanner";

function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    account,
    setAccount,
    errors: formErrors,
    touched,
    setTouched,
    onValidate,
    onTouched,
  } = useSignInForm({ email: "" });
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState("");
  const [resetDone, setResetDone] = useState(false);

  useDocumentTitle(
    t("Auth.requestForm.pageTitle"),
    "This page is for registered user to reset their password"
  );

  const handleChangeField = (e: ChangeEvent<HTMLInputElement>) => {
    if (resetDone) {
      setResetDone(false);
    }
    setAccount({ ...account, [e.target.name]: e.target.value });
  };

  const handleBlurField = (e: FocusEvent<HTMLInputElement>) => {
    onTouched(e.target.name);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const isValidated = onValidate();
    setTouched({
      email: true,
    });

    if (isValidated) {
      await sendInstructions();
    }
  };

  // The sent panel announces the first email itself, so only a resend needs a toast.
  const handleResend = async () => {
    if (await sendInstructions()) {
      showToast(t("Auth.requestSent.resent"), "success");
    }
  };

  const sendInstructions = async () => {
    setErrors("");
    setLoading(true);
    assert(account.email, "Email is required");
    try {
      await forgotPassword({ email: account.email });
      setResetDone(true);
      return true;
    } catch (error: any) {
      setErrors(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location && location?.state?.error) {
      setErrors(location?.state?.error);
      navigate(location.pathname, { state: null, replace: true });
    }
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <MainLogo className={styles.logo} />

        {errors && (
          <div className={styles.error}>
            <ApiErrorBanner error={errors} />
          </div>
        )}

        {!resetDone && (
          <>
            <div className={styles.header}>
              <h1>{t("Auth.requestForm.title")}</h1>
            </div>

            <div className={styles.description}>{t("Auth.requestForm.description")}</div>

            <form onSubmit={handleSubmit}>
              <div className={styles.formWrapper}>
                <TextInput
                  label={t("Auth.emailForm.emailLabel")}
                  error={touched.email && formErrors.email}
                  size="large"
                  name="email"
                  placeholder="johnsmith@gmail.com"
                  value={account.email}
                  onChange={handleChangeField}
                  onBlur={handleBlurField}
                  invalid={!!(formErrors.email && touched.email)}
                />

                {loading ? (
                  <Spinner className={styles.spinner} />
                ) : (
                  <Button className={styles.button} size="large" type="submit">
                    {t("Auth.requestForm.submit")}
                  </Button>
                )}
              </div>
            </form>
          </>
        )}

        {resetDone && (
          <div className={styles.resetSentContainer}>
            <Email className={styles.resetIcon} />

            <h1 className={styles.resetTitle}>{t("Auth.requestSent.title")}</h1>

            <p className={styles.resetMessage}>
              {t("Auth.requestSent.description", {
                account: { email: account.email ?? "" },
              })}
            </p>

            <Button
              className={styles.resendButton}
              variant="secondary"
              size="large"
              loading={loading}
              onClick={handleResend}
            >
              {t("Auth.requestSent.resend")}
            </Button>
          </div>
        )}
      </div>

      <div className={styles.backRow}>
        <Button as="link" variant="plain" to={routes.public.signIn.path()}>
          ← {t("Auth.actions.backToLogin")}
        </Button>
      </div>

      <FrameBack className={styles.frameBack} />
    </div>
  );
}

export default ForgotPassword;
